// Dispatch › Hours (QA-402): the technicians' hours for a week, for those who correct them (time.correct:
// the owner, a manager, the bookkeeper). Who missed a clock-out comes first, then who has flagged
// entries; a person's days open below with "Correct hours…" (none on a day whose pay period is
// locked). Minutes only: pay never shows here.

import { useEffect, useState } from 'react';
import { Figure } from '@flowology/ui-web';
import '@flowology/ui-web/base.css';
import { Header, type NavProps } from '../Nav.tsx';
import { useDesign } from '../blueprint/design.ts';
import '../blueprint/schedule.css';
import { todayIn } from '../books/model.ts';
import { minutesText } from '../books/timesheet-model.ts';
import { PersonDays } from '../books/Timesheet.tsx';
import { Loading, Panel, useQuery, useToast } from '../books/ui.tsx';
import {
  dayPick,
  isThisWeek,
  lastSevenPick,
  laterDisabled,
  needs,
  pickText,
  stepPick,
  teamOrder,
  thisWeekPick,
  totalsText,
  weekOf,
  type Picked,
  type Range,
  type TeamPerson,
} from './hours-model.ts';
import './hours.css';
import { noTechniciansLine } from '../board-extras-model.ts';

export function HoursScreen({
  nav,
  timeZone,
  open,
}: {
  nav: NavProps;
  timeZone: string;
  /**
   * The board's link: this person, over the last seven days (where a missed clock-out sits), or on one
   * day (their clock line under their name, BL-126).
   */
  open?: { staffId: string; date?: string; at: number };
}) {
  const today = todayIn(timeZone);
  const opened = (o: NonNullable<typeof open>, day: string) =>
    o.date ? dayPick(o.date) : lastSevenPick(day);
  // what was picked, kept as picked (QA-519): on a Sunday this week and the last seven days are the same days
  const [pick, setPick] = useState<Picked>(() => (open ? opened(open, today) : thisWeekPick(today)));
  const range = pick.range;
  const [staffId, setStaffId] = useState<string | null>(open?.staffId ?? null);
  useEffect(() => {
    if (!open) return;
    setPick(opened(open, todayIn(timeZone)));
    setStaffId(open.staffId);
  }, [open?.staffId, open?.date, open?.at, timeZone]);
  const [toast, setToast] = useToast();
  const bp = useDesign() === 'blueprint';
  return (
    <div className={bp ? 'app books hours-page bp-sch' : 'app books hours-page'}>
      <Header
        nav={nav}
        title={bp ? 'Hours worked' : 'Hours'}
        lede="The technicians’ hours by the week, as they tapped them, with the office’s corrections. Minutes only, never pay."
      />
      <main className="hours-main">
        <div className="page-head week-pick">
          <nav className="seg" aria-label="Week">
            <button
              className="btn quiet icon"
              aria-label="Earlier week"
              onClick={() => setPick(stepPick(pick, -1, today))}
            >
              ‹
            </button>
            <button
              className={`btn quiet${isThisWeek(pick, today) ? ' on' : ''}`}
              aria-pressed={isThisWeek(pick, today)}
              onClick={() => setPick(thisWeekPick(today))}
            >
              This week
            </button>
            <button
              className="btn quiet icon"
              aria-label="Later week"
              disabled={laterDisabled(pick, today)}
              onClick={() => setPick(stepPick(pick, 1, today))}
            >
              ›
            </button>
          </nav>
          <label className="inline-label">
            <span className="flabel">Week of</span>
            <input
              className="field"
              type="date"
              max={today}
              value={range.from}
              onChange={(e) => e.target.value && setPick({ kind: 'week', range: weekOf(e.target.value) })}
            />
          </label>
          <span className="muted">{pickText(pick)}</span>
        </div>
        <Team range={range} staffId={staffId} onPick={setStaffId} timeZone={timeZone} toast={setToast} />
      </main>
      {toast && (
        <p className="toast" role="status">
          {toast}
        </p>
      )}
    </div>
  );
}

function Team({
  range,
  staffId,
  onPick,
  timeZone,
  toast,
}: {
  range: Range;
  staffId: string | null;
  onPick: (id: string | null) => void;
  timeZone: string;
  toast: (m: string) => void;
}) {
  const team = useQuery<{ people: TeamPerson[] }>('time.team', range);
  const bp = useDesign() === 'blueprint';
  // A correction in a pay period that was already prepared: the bookkeeper prepares it again.
  const [stale, setStale] = useState(false);
  if (!team.data)
    return <Loading error={team.error} what="the technicians’ hours" retry={() => void team.reload()} />;
  const people = teamOrder(team.data.people);
  const person = people.find((p) => p.staffId === staffId);
  // Blueprint (DESIGN.md §3): the week's figures, then the technicians, the work, on the left and the
  // one picked on the right; Riverstone's panels one under the other
  const technicians = (
    <Panel title="Technicians">
      {people.length === 0 ? (
        <p className="muted">{noTechniciansLine(bp)}</p>
      ) : (
        <ul className="todo">
          {people.map((p) => (
            <li key={p.staffId}>
              <button
                className="todo-row"
                aria-pressed={p.staffId === staffId}
                onClick={() => onPick(p.staffId === staffId ? null : p.staffId)}
              >
                <span>
                  <strong>{p.name}</strong>
                  <span className="muted small">{totalsText(p.totals)}</span>
                </span>
                <span className="row needs">
                  {needs(p).map((n) => (
                    <span key={n.text} className={`chip say${n.tone ? ` ${n.tone}` : ''}`}>
                      {n.text}
                    </span>
                  ))}
                  <span aria-hidden="true">›</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
  const days = person && (
    <Panel title={`${person.name}’s days`}>
      <PersonDays
        key={`${person.staffId}-${range.from}-${range.to}`}
        person={{ id: person.staffId, displayName: person.name, isTechnician: true }}
        range={range}
        timeZone={timeZone}
        toast={toast}
        staleNote={
          stale
            ? 'That day’s pay period was already prepared: the bookkeeper prepares it again before locking its hours.'
            : null
        }
        onStale={() => setStale(true)}
        onChanged={() => void team.reload()}
      />
    </Panel>
  );
  if (!bp)
    return (
      <>
        {technicians}
        {days}
      </>
    );
  const sum = (k: keyof TeamPerson['totals']) => people.reduce((n, p) => n + p.totals[k], 0);
  const toCheck = people.reduce((n, p) => n + p.flagged + p.leftOpen, 0);
  const missed = people.some((p) => p.leftOpen > 0);
  return (
    <>
      <div className="flw bp-facts">
        <Figure label="Work" value={minutesText(sum('work'))} />
        <Figure label="Driving" value={minutesText(sum('drive'))} />
        <Figure label="At the shop" value={minutesText(sum('nonJob'))} />
        <Figure label="To check" value={<span className={missed ? 'flw-bad' : undefined}>{toCheck}</span>} />
      </div>
      <div className="bp-cols">
        <div>{technicians}</div>
        <div>
          {days || <p className="flw-empty">Pick a technician to see their days and correct them.</p>}
        </div>
      </div>
    </>
  );
}
